/**
 * M7 The First Rush: the story contracts (plan §2, S0; spec §2, §8.3, §12.1, §16.8). Every type the story layers
 * share lives here so the wave-1 and wave-2 tasks can build in parallel without touching each other's files.
 *
 * - `src/story/**` (main thread) is Career-in, Career-out: functions take a `Career` (plus a small `StoryWorldView`
 *   from the worker) and return a new `Career` with a list of `StoryCommand`s (worker commands, mirrored here so the
 *   main-thread tasks build before the worker exists; S16 adds the real ones) and `StoryEffect`s (UI work).
 * - `career.story` (`StoryState`) is the truth (spec §1 rule 11). The worker holds only what it can re-derive.
 * - Story logic is event-driven (`StoryEvent`), never per frame, so the hot-loop rule does not apply here.
 *
 * APPEND-ONLY after S0: later tasks may add members (optional, with a Zod default in `schemas.ts`) and append ids
 * to the id lists; they never rename or remove one, because saved careers carry them.
 *
 * Days: `onlineDays` counts faction days the player was online (a catch-up day is not online, plan §2). Every story
 * countdown, floor, grievance, idleRamp and keep-alive counts online days. The worker's faction day (`day` in
 * `StoryWorldView`) is runtime knowledge, passed in by the caller, never stored in the Career.
 */
import type { ComponentType, ContractType, SimFactionId } from '../data/m2Schemas'
import type { RegionId } from '../galaxy/types'
import type { CrimeKind, FactionId, GateStageName, MeasureName } from '../strategic/types'
import type { GuardianKillEntry, TetherSaleEntry } from '../career/types'

/** `{ a: 'a', b: 'b' }` from `['a', 'b']`: the symbolic form of an id list (`STORY_FLAG.manifest_sold`). */
function keyed<const T extends readonly string[]>(ids: T): { readonly [K in T[number]]: K } {
  const out: Record<string, string> = {}
  for (const id of ids) out[id] = id
  return Object.freeze(out) as { readonly [K in T[number]]: K }
}

// ---------------------------------------------------------------------------------------------
// Evidence (spec §2.1–§2.5)
// ---------------------------------------------------------------------------------------------

/** Canon §4 threads. */
export const THREADS = ['evacuation', 'watch', 'report', 'settlement'] as const
export type Thread = (typeof THREADS)[number]
export const THREAD = keyed(THREADS)

/** The evidence chain's four links (spec §2.5), in hearing order. */
export const CASE_LINKS = ['order', 'knowledge', 'coverup', 'complicity'] as const
export type CaseLink = (typeof CASE_LINKS)[number]
export const CASE_LINK = keyed(CASE_LINKS)

/** A log's link: one of the four, or `none` (texture, official, most leads). */
export const LOG_LINKS = [...CASE_LINKS, 'none'] as const
export type LogLink = (typeof LOG_LINKS)[number]

/** Spec §2.1 `kind`. */
export const LOG_KINDS = ['lead', 'proof', 'texture', 'official', 'counter'] as const
export type LogKind = (typeof LOG_KINDS)[number]
export const LOG_KIND = keyed(LOG_KINDS)

/**
 * Spec §2.1 `source` (the Logs screen's tabs, §2.6). `holdfastContract` (the guaranteed Watch logs, §5) and
 * `mutualArchive` (the repeatable `order` corroboration, §2.5) are named sources the spec lists in prose.
 */
export const LOG_SOURCES = [
  'derelictCore',
  'convoyWreck',
  'guardianWreck',
  'kellComms',
  'frColonyRuin',
  'frResearchDerelict',
  'authorityArchive',
  'wireLeak',
  'missionItem',
  'yourRecords',
  'frStationBootLog',
  'holdfastContract',
  'mutualArchive',
] as const
export type LogSource = (typeof LOG_SOURCES)[number]
export const LOG_SOURCE = keyed(LOG_SOURCES)

/** Where a log's lead points (spec §2.1 `lead.poiKind`): the M3 `PoiKind`s plus the story's own site kinds. */
export const LEAD_POI_KINDS = [
  'derelict',
  'cache',
  'anomaly',
  'hidden-base',
  'nav-beacon',
  'station',
  'wreckField',
  'ruin',
  'relay',
  'anchor',
] as const
export type LeadPoiKind = (typeof LEAD_POI_KINDS)[number]

/** Evidence data files (plan D-TBD: split per thread so content builders own disjoint files; `boot` = §16.6). */
export const EVIDENCE_FILES = ['evacuation', 'settlement', 'watch', 'report', 'boot'] as const
export type EvidenceFile = (typeof EVIDENCE_FILES)[number]

/** Comms data files (`src/data/story/comms/*.json`): shared lines, one per mission, the finale. */
export const COMMS_FILES = ['common', 'm1', 'm2', 'm3', 'm4', 'finale'] as const
export type CommsFile = (typeof COMMS_FILES)[number]

/** The Case line (spec §2.5): never a number in UI. Thresholds in `case.json`. */
export const CASE_LINES = ['rumour', 'circumstantial', 'documented', 'damning'] as const
export type CaseLine = (typeof CASE_LINES)[number]

// ---------------------------------------------------------------------------------------------
// Story flags (spec §4–§8, §11, §16). Grouped by the task that sets them; append new flags inside a group.
// ---------------------------------------------------------------------------------------------

export const STORY_FLAGS = [
  // Mission 1, The Manifest (§4; S12)
  'manifest_recovered',
  'manifest_sold',
  // Mission 2, Watchers (§5; S12)
  'watchers_helped',
  'watchers_stood_off',
  'watchers_betrayed',
  'hale_logged_route',
  // Mission 3, The Transferred Team (§6; S13)
  'report_original',
  'okafor_public',
  'okafor_anonymous',
  'hale_aware',
  'okafor_warned',
  'voss_contacted',
  // Mission 4, Sealed Records (§7; S13). `archive_legal` is the legal-route alert (§10.2).
  'archive_legal',
  'archive_back',
  'settlement_file',
  'hale_deal',
  'hale_refused',
  'hale_knows',
  'duarte_exposed',
  'hale_deal_voided',
  // Claims (§2.3; S7): Kell's or the archivist's assist on a tier's claim.
  'claim_assisted_2',
  'claim_assisted_3',
  'claim_assisted_4',
  'claim_assisted_5',
  // First-hour hooks and Kell's nudges (§2.2, §11; S14).
  'first_core_logged',
  'first_ruin_logged',
  'harrow_hook_posted',
  'kell_nudge_1',
  'kell_nudge_2',
  'kell_nudge_3',
  'kell_nudge_4',
  // Finale (§8; S21, S25). The path list is `finale.paths`; these are the facts other systems read.
  'authority_exposed',
  'voss_blackmailed',
  'guardian_joined',
  'stipend_known',
  'duarte_testifies',
  'on_payroll',
  'ban_passed',
  'ban_failed',
  // Reactivation (§16; S15, S20, S23, S24).
  'harrow_surveyed',
  'candle_run_offered',
] as const
export type StoryFlag = (typeof STORY_FLAGS)[number]
export const STORY_FLAG = keyed(STORY_FLAGS)

/** Set flags: present = set. Absent keys are unset (no `false` values are stored). */
export type StoryFlags = Partial<Record<StoryFlag, true>>

/** Reyes after Watchers (§5 `reyes_state`). Absent = not met. */
export const REYES_STATES = ['ally', 'cool', 'hostile', 'dead'] as const
export type ReyesState = (typeof REYES_STATES)[number]

/** Okafor (§6 `okafor_state`). Absent = not met. */
export const OKAFOR_STATES = ['hideout', 'aboard', 'safe', 'held', 'lost'] as const
export type OkaforState = (typeof OKAFOR_STATES)[number]

// ---------------------------------------------------------------------------------------------
// Story items (spec §2.8; S4)
// ---------------------------------------------------------------------------------------------

/** The mission items (spec §2.8). Names are placeholders until the naming pass; ids never change. */
export const STORY_ITEM_IDS = ['column_nine_recorder', 'kell_package', 'report_core', 'settlement_50a', 'lab_notes'] as const
export type StoryItemId = (typeof STORY_ITEM_IDS)[number]
export const STORY_ITEM = keyed(STORY_ITEM_IDS)

/** `withheld` = Hale holds the original (§7); `copy` = the player holds a partial copy (weight × the copy factor). */
export const ITEM_STATES = ['held', 'copy', 'sold', 'lost', 'withheld'] as const
export type ItemState = (typeof ITEM_STATES)[number]
export const ITEM_STATE = keyed(ITEM_STATES)

export interface StoryItem {
  id: StoryItemId
  state: ItemState
  /** Where it is when not in the hold: a death pod id, `hale`, a station id. */
  where?: string
  /** A patrol scan reads it (§2.8, §5): a fine, never confiscation. */
  contraband?: boolean
  /**
   * S4: the death-wreck pod holding the player's original or copy (`death:<n>`, the n-th death), until the pod is
   * back in the hold. While set, the hold mirror is expected inside that pod, not in the hold.
   */
  pod?: string
}

// ---------------------------------------------------------------------------------------------
// Missions (spec §3, §13 task 8; S6)
// ---------------------------------------------------------------------------------------------

export const MISSION_IDS = ['m1', 'm2', 'm3', 'm4'] as const
export type MissionId = (typeof MISSION_IDS)[number]

/** Absent from `missions` = not offered yet. */
export const MISSION_STATES = ['offered', 'active', 'complete'] as const
export type MissionState = (typeof MISSION_STATES)[number]

/** Plan §2: the verbs a mission step runs on (existing verbs only, spec §1 rule 1). */
export const STEP_VERBS = [
  'scan',
  'salvage',
  'recover',
  'deliver',
  'courier',
  'laneRepair',
  'relayParts',
  'dock',
  'comms',
  'clearRaiders',
  'reboot',
  'claim',
] as const
export type StepVerb = (typeof STEP_VERBS)[number]
export const STEP_VERB = keyed(STEP_VERBS)

export interface StoryMission {
  id: MissionId
  /** The current step id (`missions/<id>.json steps[].id`); the last step's id once complete. */
  step: string
  state: MissionState
  /** Fork id → the choice taken. */
  choices?: Record<string, string>
  /** S6: the fork offered after the current step (its costs shown, no choice yet). The step is done while set. */
  awaitingFork?: string
}

/** Per-tier claim attempts (spec §2.3; S7). */
export interface StoryClaim {
  /** 2..5. */
  tier: number
  /** Total attempts at this tier. */
  attempts: number
  /** The faction day of the last attempt (the retry rule's "next faction day"). */
  lastAttemptDay: number
  /** Attempts made on `lastAttemptDay` (at most `claims.json attemptsPerDay`). */
  dayAttempts: number
  /** `readLogs.length` at the last attempt: a retry needs a log read after it (re-reading never counts). */
  readMark: number
  /** The online day of the second failed attempt (Kell's safety net waits 3 online days from it). */
  failOnlineDay?: number
  /** Solved through Kell or the archivist (`claim_assisted_<tier>`). */
  assisted: boolean
}

/** What a countdown is for (S6 runs them on online days; S11 schedules callbacks through them). */
export const COUNTDOWN_KINDS = ['okaforWarning', 'okaforCustody', 'callback', 'floor', 'surcharge', 'retaliation', 'reactivation', 'aftermath'] as const
export type CountdownKind = (typeof COUNTDOWN_KINDS)[number]

export interface StoryCountdown {
  /** Unique within the career (`okafor:warning`, `cb:<callbackId>`, ...). */
  id: string
  kind: CountdownKind
  startOnlineDay: number
  /** Fires when `onlineDays ≥ dueOnlineDay`; never during a catch-up (spec §10.4). */
  dueOnlineDay: number
  /** What it refers to (a callback id, a POI id, a path). */
  ref?: string
}

// ---------------------------------------------------------------------------------------------
// The gate clock (spec §10; S10)
// ---------------------------------------------------------------------------------------------

/** Alert steps (§10.2): +0.15 each while set. `okaforPublicAboard` = `okafor_public` with Okafor aboard. */
export const CLOCK_ALERTS = ['manifestSold', 'legalArchive', 'okaforPublicAboard'] as const
export type ClockAlert = (typeof CLOCK_ALERTS)[number]

/** Post-finale clock rows (§10.2). Combined rows (§8.3) are derived from `finale`, never stored as their own row. */
export const FINALE_ROWS = ['exposePassed', 'exposeFailed', 'blackmail', 'guardiansFloor', 'guardians', 'sell', 'exposePending'] as const
export type FinaleRow = (typeof FINALE_ROWS)[number]

export interface StoryClock {
  alerts: ClockAlert[]
  /** Online days spent at tiers 0–2 (the idleRamp, §10.2). */
  idleDays: number
  /** The newest post-finale row (S10 derives combined rows from `finale`). Absent = before the finale. */
  finaleRow?: FinaleRow
  /** Tiers whose one-off stability drop has fired (once per tier per career). */
  tierDropsFired: number[]
}

// ---------------------------------------------------------------------------------------------
// The finale and its ladder (spec §8; S21, S25)
// ---------------------------------------------------------------------------------------------

export const FINALE_PATHS = ['expose', 'blackmail', 'guardians', 'sell'] as const
export type FinalePath = (typeof FINALE_PATHS)[number]
export const FINALE_PATH = keyed(FINALE_PATHS)

/** The Expose vote's outcome as the Career knows it (the bridge maps the worker's result). */
export const VOTE_OUTCOMES = ['pending', 'passed', 'failed'] as const
export type VoteOutcome = (typeof VOTE_OUTCOMES)[number]

export interface StoryStipend {
  /** Tethys's AI-only tetherstone income over the 10 faction days before the commit (spec §8.2, T17). */
  base: number
  /** The share fixed at commit. */
  share: number
  fixedOnlineDay: number
  /** Credits paid so far (the clawback debt on leaving Blackmail equals this, §8.3). */
  paid: number
}

export interface StoryFinale {
  /** Paths in force, in commit order (spec §8.3). Empty = no commit yet. */
  paths: FinalePath[]
  /** The path left (only `blackmail` can be left). */
  from?: FinalePath
  /** The online day the Blackmail exit was paid (the +0.5 surcharge runs 30 online days from it). */
  exitOnlineDay?: number
  /** The guardian aggression floor's last online day (the Guardians row is 1.3 while it holds). */
  floorUntilOnlineDay?: number
  vote?: VoteOutcome
  stipend?: StoryStipend
  /** S25: post-finale repeatables taken (`finale.json repeatables` id → count and the last online day). */
  repeats?: Record<string, { count: number; lastOnlineDay: number }>
  /** S25: the online day the Candle went dark (the Watch learned of the stipend; world flag `candle_dark`). */
  candleDarkOnlineDay?: number
  /**
   * S39: the corporation screen's ballot stance toggle (spec §8.2). Absent = the default for the paths in force
   * (`bridge.json ballot`); set, it overrides that default on every re-apply. A measure left out keeps its default.
   */
  ballot?: Partial<Record<MeasureName, BallotStance>>
}

/** One bundle entry (spec §2.5): one entry per item id; an original replaces its copy. */
export interface BundleEntry {
  kind: 'log' | 'item'
  id: string
}

/** A bundle picked at a hand-in (up to `case.json bundleMax` entries). */
export interface StoryBundle {
  path: FinalePath
  entries: BundleEntry[]
}

// ---------------------------------------------------------------------------------------------
// Tethys leverage (spec §9; S5)
// ---------------------------------------------------------------------------------------------

/** Career stages (spec §9), read at the moment of the offer or retaliation. */
export const CAREER_STAGES = ['leased', 'owned', 'corporation', 'fleetNotes'] as const
export type CareerStage = (typeof CAREER_STAGES)[number]

/** Retaliation rows (spec §9). The worker half is a bit per row (`setRetaliation`). */
export const RETALIATION_ROWS = [
  'principalDue',
  'assetRecoveryHunt',
  'insuranceCancelled',
  'dockingRefused',
  'refinerySalesRefused',
  'boardsClosed',
  'interdiction',
  'componentsRefused',
  'yardsRefused',
  'claimPressure',
  'sabotage',
  'fleetNotesCalled',
] as const
export type RetaliationRow = (typeof RETALIATION_ROWS)[number]

/**
 * What a station is asked to do (S14 `retaliationRefuses`): dock, a market sale or purchase, the contract board, a
 * shipyard order. Every station service needs a dock, so the `dockingRefused` row refuses them all.
 */
export const STATION_ACTIONS = ['dock', 'sellWare', 'buyWare', 'sellComponent', 'buyComponent', 'contracts', 'yardOrder'] as const
export type StationAction = (typeof STATION_ACTIONS)[number]

export interface StoryRetaliation {
  rows: RetaliationRow[]
  /** × 1.5 after a Blackmail exit (§8.3). A second path applies only the difference (retaliation lands once). */
  mult: number
  sinceOnlineDay: number
}

/** Debts the story creates (the Blackmail clawback, a called principal, a voided deal's fee, called fleet notes). */
export const STORY_DEBT_KINDS = ['clawback', 'principalCalled', 'registryFee', 'fleetNotes'] as const
export type StoryDebtKind = (typeof STORY_DEBT_KINDS)[number]

export interface StoryDebt {
  kind: StoryDebtKind
  creditor: SimFactionId
  /** Credits still owed. */
  amount: number
  /** Payment cycles left before collections (N ≥ 2, spec §9). */
  cyclesLeft: number
  /** Bought by the Mutual (spec §9 refinancing): the premium applies and the obligation contract runs. */
  refinanced?: boolean
  /** The cycle the next Mutual obligation contract is due by. */
  obligationDueCycle?: number
}

/** Hale's deal (spec §7; S13). */
export interface HaleDeal {
  /** The faction day of the offer: only bounties older than it are wiped (T16). */
  offerDay: number
  /** Charters still refunded with the standing gate waived (`leverage.json hale.charters`). */
  chartersLeft: number
  /** Bounty credits wiped (restored × 2 when the deal is voided). */
  bountiesWiped: number
  /** The corporation founding fee waived (a pilot without a corporation); falls due when voided. */
  feeWaived: number
  /** S13: commission ids of the charters bought under the deal (put under review when it is voided). */
  charterIds?: string[]
}

/** A meter reading counted as a `watch` proof (spec §2.4: one per distinct Tethys operation per 10 faction days). */
export interface MeterProof {
  opId: string
  day: number
}

/** A repeatable corroboration taken (spec §2.5: once per 10 online days, at most 2 per link). */
export interface Corroboration {
  link: CaseLink
  onlineDay: number
}

/**
 * Kell's nudge and hint ladder at one tier (spec §11; S14). Reset when the tier changes. The nudge itself is marked
 * by the `kell_nudge_<tier>` flag; this block only times it and counts the hint rungs sent after it.
 */
export interface StoryNudge {
  /** The tier (1–5, a new career reads as 1) the counters below belong to. */
  tier: number
  /** The online day the hooks first saw this tier. */
  sinceOnlineDay: number
  /** Hint rungs sent at this tier (0 = none yet). */
  rungs: number
  /** The online day of the last nudge or hint at this tier. */
  lastOnlineDay: number
}

// ---------------------------------------------------------------------------------------------
// Derelict reactivation (spec §16; S15, S20, S23, S24)
// ---------------------------------------------------------------------------------------------

export const REACTIVATION_STATES = ['surveyed', 'kit', 'lit', 'title', 'handedOver', 'station', 'dark', 'reverted'] as const
export type ReactivationState = (typeof REACTIVATION_STATES)[number]
export const REACTIVATION_STATE = keyed(REACTIVATION_STATES)

export const REGISTRANTS = ['title', 'corporation', 'handover'] as const
export type Registrant = (typeof REGISTRANTS)[number]
export const REGISTRANT = keyed(REGISTRANTS)

/** First Rush station classes (spec §16.2), mapped to M4 templates in `reactivation.json`. */
export const FR_CLASSES = ['habRing', 'oreProcessor', 'depot', 'lab'] as const
export type FrClass = (typeof FR_CLASSES)[number]

/** Kit progress at a derelict's repair port (persists between trips, spec §16.3 step 4). */
export interface ReactivationKit {
  /** Salvaged components delivered, per type. */
  components: Partial<Record<ComponentType, number>>
  /** Goods delivered (`fuel_cells`, `electronics`; `GoodId`s once the economy plan's goods block lands). */
  goods: Record<string, number>
}

export interface StoryReactivation {
  poiId: string
  state: ReactivationState
  /** Absent until registered. */
  registrant?: Registrant
  /** Hand-over recipient. */
  recipient?: FactionId
  /** The online day it was last lit or supplied (title keep-alive, §16.5). */
  lastLitDay: number
  frClass?: FrClass
  kit?: ReactivationKit
  /** S15: a re-restart after a revert (the kit is `rerestartMult` of the full one, spec §16.4). */
  rerestart?: true
  /** S24: the site carries a Holdfast generational claim marker (the re-apply re-sends it). */
  marker?: true
  /** S24: a corporation runs the marked site with its family (the re-apply re-sends it, keeping the family share). */
  withFamily?: true
  /** S40: the reactor section's coolant anchor was vented (spec §16.3 step 2). */
  vented?: true
  /** S40: how the squat was cleared (Wreckers fought or paid off; a Holdfast family's comms scene). */
  cleared?: SquatCleared
  /**
   * S43: a title released at corporation founding (over `title.max`, or with no M6 station for it): lit and
   * unregistered, and only a hand-over may register it (the worker kept its keep-alive clock).
   */
  handoverOnly?: true
}

/** S40: how a squat was cleared (spec §16.3 step 2). */
export const SQUAT_CLEARED = ['fought', 'paid', 'family'] as const
export type SquatCleared = (typeof SQUAT_CLEARED)[number]

// ---------------------------------------------------------------------------------------------
// The Career story block (spec §12.1, §2.8, §8.3, §16.8)
// ---------------------------------------------------------------------------------------------

export interface StoryState {
  flags: StoryFlags
  items: StoryItem[]
  missions: StoryMission[]
  claims: StoryClaim[]
  countdowns: StoryCountdown[]
  clock: StoryClock
  bundles: StoryBundle[]
  finale: StoryFinale
  reactivations: StoryReactivation[]
  /** Faction days the player was online (plan §2). */
  onlineDays: number
  /** Logs the player has opened, in reading order (ids from `evidence/*.json`). */
  readLogs: string[]
  /** Logs in the ledger not opened yet (a picked-up core's log lands here, §2.1). Disjoint from `readLogs`. */
  unreadLogs: string[]
  /** Log ids whose `lead` is still open (the Logs screen's "Open leads"). */
  leads: string[]
  reyesState?: ReyesState
  okaforState?: OkaforState
  retaliation?: StoryRetaliation
  debts?: StoryDebt[]
  hale?: HaleDeal
  meterProofs?: MeterProof[]
  corroborations?: Corroboration[]
  /** S12: Holdfast contracts handed in (spec §5 "before Watchers": the 1st and 3rd drop a guaranteed Watch log). */
  holdfastContracts?: number
  /** S14: Kell's nudge and hint-ladder progress at the current tier (spec §11). */
  nudge?: StoryNudge
  /** S13: the faction day of Hale's call (the M4 offer); only bounties older than it can be wiped (T16). */
  haleOfferDay?: number
  /** S37: the M1 and M2 lasting traces and the online day each began (the stripped field's clock). */
  traces?: StoryTraces
}

/** S37: a mission's lasting trace in the world (spec §4, §5): its kind and the online day it began. */
export interface StoryTraces {
  columnNine?: { kind: 'memorial' | 'stripped'; onlineDay: number }
  barge?: { kind: 'wreck' | 'rig'; onlineDay: number }
  /** S37 part 2: Mission 1 issued its accept-time commands (the Asset Recovery crew) on this online day; once. */
  m1Crew?: { onlineDay: number }
  /** S37d: the Mission 2 barge wreck was looted (every part taken) on this online day; it never respawns. */
  bargeLooted?: { onlineDay: number }
}

// ---------------------------------------------------------------------------------------------
// Career logging shapes (spec §12.1; S1). The kill and sale logs predate M7 (D-340, `career/logs.ts`).
// ---------------------------------------------------------------------------------------------

/** The plan's `GuardianKill`: `career.guardianKills[]` (D-340 shape plus the optional M7 fields). */
export type GuardianKill = GuardianKillEntry
/** The plan's `TetherSale`: `career.tetherSales[]` (D-340 shape plus `bunker`). */
export type TetherSale = TetherSaleEntry

/** Running totals beside the 64-entry sale ring (plan S1). */
export interface TetherTotals {
  tonnes: number
  credits: number
  sales: number
}

/** Income sources for the §9 owned-pilot retaliation rows. */
export const INCOME_SOURCES = ['mining', 'combat', 'salvage', 'trade', 'contracts'] as const
export type IncomeSource = (typeof INCOME_SOURCES)[number]

export type IncomeBucket = Record<IncomeSource, number>

/** Rolling income (spec §12.1): `logging.json buckets` × `bucketS` of game time (~5 h). Oldest bucket first. */
export interface IncomeMix {
  /** Game time the newest bucket started. */
  bucketStart: number
  buckets: IncomeBucket[]
}

// ---------------------------------------------------------------------------------------------
// Events, commands, effects
// ---------------------------------------------------------------------------------------------

/** Comms speakers (placeholder names; ids are stable). */
export const SPEAKERS = [
  'kell',
  'reyes',
  'okafor',
  'duarte',
  'hale',
  'voss',
  'mutualArchivist',
  'mutualScrapyard',
  'assetRecovery',
  'meridianCounsel',
  'meridianDesk',
  'charterPatrol',
  'recordsDesk',
  // S9: generic station refusals (§3.1) and the Mutual relay crew's back route (§7).
  'stationControl',
  'mutualRelayCrew',
] as const
export type Speaker = (typeof SPEAKERS)[number]

/** Wire outlets (spec §12.3), each with slanted headlines. */
export const WIRE_OUTLETS = ['authorityGazette', 'mutualBulletin', 'tethysComms', 'candleRelay'] as const
export type WireOutlet = (typeof WIRE_OUTLETS)[number]

/** Triggers that aren't flags (callbacks keyed on events, §16.6, §8.3). */
export const STORY_TRIGGERS = [
  'tierClaimed',
  'finaleCommit',
  'ladderMove',
  'restartLit',
  'handoverHoldfast',
  'handoverTethysVein',
  'markedSelfRestart',
  'okaforLabRestart',
] as const
export type StoryTrigger = (typeof STORY_TRIGGERS)[number]

/**
 * What advances mission steps (plan §2; S30 maps them from existing game events). Every `day` is a faction day.
 */
export type StoryEvent =
  | { type: 'contractDone'; contractType: ContractType; stationId: string; factionId: SimFactionId }
  | { type: 'scanned'; targetKind: string; targetId: string; systemId: string; scannerTier: number }
  | { type: 'salvaged'; componentType: ComponentType; partId: string; systemId: string; poiId?: string }
  | { type: 'docked'; stationId: string; systemId: string }
  | { type: 'crime'; kind: CrimeKind; systemId: string; witnessed: boolean }
  | { type: 'killed'; factionId: FactionId; systemId: string; role?: string; storyShip?: string }
  | { type: 'itemPicked'; itemId: StoryItemId }
  | { type: 'commsReply'; nodeId: string; replyId: string }
  | { type: 'claimMade'; tier: number; correct: boolean }
  | { type: 'onlineDay'; onlineDay: number; day: number }
  | { type: 'laneRepaired'; ringId: string; systemId: string }
  | { type: 'delivered'; targetId: string; good: string; qty: number }
  | { type: 'rebooted'; targetId: string; ok: boolean }
  | { type: 'raidersCleared'; systemId: string; siteId?: string }
  | { type: 'logRead'; logId: string }
  | { type: 'meterReading'; opId: string; systemId: string; day: number }

/** Ballot stances for the player corporation (spec §8.2). */
export const BALLOT_STANCES = ['for', 'against', 'abstain'] as const
export type BallotStance = (typeof BALLOT_STANCES)[number]

/** Contestable claim states (spec §12.2). `none` clears. */
export const CLAIM_STATES = ['none', 'underReview', 'harbouring'] as const
export type ClaimStateName = (typeof CLAIM_STATES)[number]

/** Mission-owned traffic (plan D-TBD: one command, never ambient spawns). */
export const STORY_SHIP_ROLES = [
  'assetRecovery',
  'lesseeBarge',
  'prisonerTransport',
  'vossYacht',
  'meridianCourier',
  'missionRig',
  'varroContractors',
] as const
export type StoryShipRole = (typeof STORY_SHIP_ROLES)[number]

/** A faction or the player (pressure, grievances and transfers involve the player's side too). */
export type StoryParty = FactionId | 'player'

/**
 * The worker commands the story sends (a typed mirror of S16's M7 commands plus the M5 ones it reuses). Every one
 * is idempotent when re-applied (`set*`), except the one-offs the bridge sends once (`seedPressure`, transfers).
 */
export type StoryCommand =
  | { type: 'setStoryTier'; tier: number }
  | { type: 'setStoryClock'; storyMult: number }
  | { type: 'storyTierDrop'; tier: number; drop: number; cause: string }
  | { type: 'setWorldFlag'; key: string; value: number | string | boolean }
  | { type: 'postWire'; headline: string; severity: number; source: WireOutlet; systemId?: string }
  | { type: 'proposeCharterVote'; measure: MeasureName; sponsor: StoryParty; evidenceWeight: number }
  | { type: 'seedPressure'; a: StoryParty; b: StoryParty; amount: number }
  | { type: 'setGrievance'; a: StoryParty; b: StoryParty; floor: number; untilOnlineDay: number }
  | { type: 'setAggressionFloor'; faction: FactionId; value: number; untilOnlineDay: number }
  | { type: 'setRetaliation'; faction: FactionId; rows: RetaliationRow[] }
  | { type: 'setPlayerBallot'; measure: MeasureName; stance: BallotStance }
  | { type: 'setClaimState'; scope: 'claim' | 'commission'; id: string; state: ClaimStateName; untilOnlineDay: number | null }
  | { type: 'spawnStoryShip'; key: string; role: StoryShipRole; systemId: string; missionId?: MissionId }
  | { type: 'hiddenTransfer'; from: StoryParty; to: FactionId; amount: number }
  | { type: 'seedBounty'; faction: FactionId; amount: number; reason: string }
  | { type: 'wipeBounties'; faction: FactionId; olderThanDay: number; cap: number }
  | { type: 'restoreBounties'; faction: FactionId; amount: number }
  | { type: 'registerHoldfastClaims'; registered: boolean }
  | { type: 'setCharterWaiver'; charters: number; waiveMinStanding: boolean }
  | { type: 'restoreDerelict'; poiId: string; systemId: string; template: string; owner: StoryParty }
  | {
      type: 'registerReactivation'
      poiId: string
      registrant: Registrant
      recipient?: FactionId
      fee: number
      /** S24: the site carries a Holdfast generational claim marker. */
      marker?: boolean
      /** S24: a corporation runs the marked site with its family. */
      withFamily?: boolean
      /** S24: the petty registry's wait (online days). */
      waitOnlineDays?: number
      /** S26: the bridge rebuilding a registration the Career holds; the worker moves no money and no standing. */
      reapply?: boolean
    }
  | { type: 'settleRegistryChallenge'; poiId: string; fee: number }
  | { type: 'supplyStation'; poiId: string; good: string; qty: number }
  /** S39d: Expose → Guardians lowers the pending Expose vote's evidence weight (the worker clamps at 0). */
  | { type: 'lowerVoteWeight'; measure: MeasureName; drop: number }
  | { type: 'storyDev'; op: 'setOnlineDay'; day: number }
  | { type: 'storyDev'; op: 'clearStory' }

/** UI work the story asks for (S30 routes them). */
export type StoryEffect =
  | { type: 'comms'; speaker: Speaker; nodeId: string }
  | { type: 'wire'; headline: string; source: WireOutlet; severity: number; systemId?: string }
  | { type: 'autosave'; reason: string }
  | { type: 'navLead'; logId: string; systemHint: string; poiKind: LeadPoiKind }
  | { type: 'awayWarning'; text: string }
  // S6: the mission runtime. `missionStep` asks the link to (re)create the current step's job (no deadline, never
  // in `contracts.active`); `missionFork` shows a fork's choices with their cost text before any commit.
  | { type: 'missionOffered'; missionId: MissionId; contact: Speaker }
  | { type: 'missionStep'; missionId: MissionId; stepId: string; reoffer: boolean }
  | { type: 'missionFork'; missionId: MissionId; forkId: string; choices: { id: string; cost: string }[] }
  | { type: 'missionComplete'; missionId: MissionId }
  // S11: a fired comms or log callback (`callbacks.json`): a one-off hail line or a texture record. Never evidence:
  // it adds no log, lead or proof (T33).
  | { type: 'callback'; callbackId: string; channel: 'comms' | 'log'; speaker?: Speaker; text: string }

/** A story function's result: the new Career plus what to send and show. */
export interface StoryResult<C> {
  career: C
  commands: StoryCommand[]
  effects: StoryEffect[]
}

/**
 * The small worker view the main-thread story reads (plan §2 `StoryView`; S16 makes the worker's getter match).
 */
export interface StoryWorldView {
  /** The worker's faction day. */
  day: number
  onlineDay: number
  stability: number
  stage: GateStageName
  /**
   * The player corporation's faction, or null before founding (spec §8.2 corporation variants). The worker's
   * `StoryView.corpFaction` is `'player'` once founded (the M6 slot, `CorpFactionId`); S26 widened this to match.
   */
  corpFaction: StoryParty | null
  /** Tethys's AI-only tetherstone income over the last 10 faction days (the stipend's base, T17). */
  tethysAiTetherIncome10d: number
  storyRigs: number
  /** POI ids of reactivated stations the worker holds. */
  reactivatedStations: string[]
  /** S37: spawned mission-owned ships by agent seq (`shipKilled.storyShip`; the worker's `StoryView.storyShips`). */
  storyShips?: { key: string; role: string; agentSeq: number }[]
  /** S39d: the newest resolved tetherstone ban vote's yes and no weights (the worker's `StoryView.banBallots`). */
  banBallots?: { for: number; against: number }
  /** S43: the world flag `candle_dark` is set (the worker's `StoryView.candleDark`; the Candle's look). */
  candleDark?: boolean
}

/** Spec §3.1 contact points (S9 resolves the primary contact or its fallback). */
export const CONTACT_INTERACTIONS = [
  'claim2',
  'claim3',
  'claim4',
  'claim5',
  'safetyNet',
  'm1Decode',
  'm2Offer',
  'm2Package',
  'm3Okafor',
  'm3Rescue',
  'm4Duarte',
  'm4Archive',
  'haleCall',
  'vossContact',
  'rollCall',
  'exposeHandIn',
  'blackmail',
  'guardians',
  'sell',
  'registration',
] as const
export type ContactInteraction = (typeof CONTACT_INTERACTIONS)[number]

/** Regions as the reactivation shares read them, plus the descendant enclaves (spec §16.6). */
export type MarkerRegion = RegionId | 'enclave'
